import {
  AfterContentChecked,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  WritableSignal,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import {DateAdapter} from '@angular/material/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import {Subscription} from 'rxjs';
import {OidcAuthService} from './shared/services/infrastructure/auth/oidc-auth/oidc-auth.service';
import {JwtService} from './shared/services/infrastructure/auth/jwt/jwt.service';
import {AppTranslateService} from './shared/services/infrastructure/page/i18n/translate/app-translate/app-translate.service';
import {Language} from './shared/common/enums/language.enum';
import {BrowserService} from './shared/services/infrastructure/page/browser/browser/browser.service';
import {IframeService} from './shared/services/infrastructure/iframe/iframe.service';
import {IframeAuthService} from './shared/services/infrastructure/auth/iframe-auth/iframe-auth.service';
import {HeaderService} from './shared/services/infrastructure/page/chrome/header/header.service';
import {SharedLinkService} from './shared/services/infrastructure/sharing/shared-link.service';
import {FooterService} from './shared/services/infrastructure/page/chrome/footer/footer.service';
import {UrlFunctions} from './shared/common/functions/url';
import {PublicEntrypointService} from './shared/services/infrastructure/auth/public-entrypoint/public-entrypoint.service';
import {FlowableVariablesService} from './shared/services/domain/flowable/core/variables/flowable-variables.service';
import {RouterNavigationService} from './shared/services/infrastructure/page/routing/router-navigation/router-navigation.service';
import {Links} from './shared/common/constants/links';
import {HeaderMenuComponent} from './shared/components/core-page/header-menu/header-menu.component';
import {FooterComponent} from './shared/components/core-page/footer/footer.component';
import {AlertComponent} from './shared/modules/alert/components/alert/alert.component';
import {GlobalLoaderService} from './shared/services/infrastructure/page/chrome/global-loader/global-loader.service';
import {SpinnerComponent} from './shared/components/basic/spinner/spinner.component';
import {PreviousRouteService} from './shared/services/infrastructure/page/routing/route/previous-route.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterOutlet,
    HeaderMenuComponent,
    FooterComponent,
    AlertComponent,
    SpinnerComponent,
  ],
})
export class AppComponent implements OnInit, OnDestroy, AfterContentChecked {

  private translate: AppTranslateService = inject(AppTranslateService);

  private oidcAuthService: OidcAuthService = inject(OidcAuthService);

  private jwtService: JwtService = inject(JwtService);

  private browserService: BrowserService = inject(BrowserService);

  private iframeService: IframeService = inject(IframeService);

  private iframeAuthService: IframeAuthService = inject(IframeAuthService);

  private headerService: HeaderService = inject(HeaderService);

  private footerService: FooterService = inject(FooterService);

  private sharedLinkService: SharedLinkService = inject(SharedLinkService);

  private renderer: Renderer2 = inject(Renderer2);

  private dateAdapter = inject<DateAdapter<any>>(DateAdapter);

  private cdRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private publicEntrypointService: PublicEntrypointService = inject(PublicEntrypointService);

  private flowableVariablesService: FlowableVariablesService = inject(FlowableVariablesService);

  private globalLoaderService: GlobalLoaderService = inject(GlobalLoaderService);

  private routerNavigationService: RouterNavigationService = inject(RouterNavigationService);

  private previousRouteService: PreviousRouteService = inject(PreviousRouteService);

  private router = inject(Router);

  private loading: WritableSignal<boolean> = signal<boolean>(true);

  private loadingRouter: WritableSignal<boolean> = signal<boolean>(true);

  private globalLoading: WritableSignal<boolean> = signal<boolean>(false);

  private globalLoaderSuppressed: WritableSignal<boolean> = signal<boolean>(false);

  private readonly defaultLang: string = Language.en;

  private readonly defaultOpenLinkLang: string = Language.de;

  private subscriptions: Subscription[] = [];

  constructor() {
    this.initLoaderConfig();
    this.initRoutingSubscription();
    this.translate.setFallbackLang(this.defaultLang);
    this.translate.addLangs();
    this.dateAdapter.setLocale(this.defaultLang);
  }

  async ngOnInit(): Promise<void> {
    this.renderer.addClass(document.body, this.getBrowser());
    this.iframeAuthService.deleteIframeToken();

    if (this.iframeService.isIframePage()) {
      await this.initIframeAuthProcess();
      return;
    }

    if (this.sharedLinkService.isSharedLinkPage()) {
      await this.initSharedLinkAuthProcess();
      return;
    }

    if (this.isErrorPage() || this.isOpenComponentsPage() || this.isIframePage() || this.isPublicEntryPointPage()) {
      this.setDefaultWindowFlowableEndpoint();
    }

    if (this.isErrorPage() || this.isIframePage() || this.isPublicEntryPointPage()) {
      this.loading.set(false);
      return;
    }

    await this.initAuthProcess();
  }

  ngAfterContentChecked(): void {
    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Method: Initializes the navigation loader by subscribing to the router events.
   * When a navigation starts, sets the loadingRouter flag to true.
   * When a navigation ends, cancels, or encounters an error, sets the loadingRouter flag to false.
   */
  private initLoaderConfig(): void {
    const navigationLoaderSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.loadingRouter.set(true);
      } else if (event instanceof NavigationEnd
        || event instanceof NavigationCancel
        || event instanceof NavigationError) {
        this.loadingRouter.set(false);
      }
    });
    this.subscriptions.push(navigationLoaderSub);

    const loadingSub = this.globalLoaderService.globalLoadingSubject.subscribe(loading => {
      this.globalLoading.set(loading);
    });
    this.subscriptions.push(loadingSub);

    const suppressSub = this.globalLoaderService.globalLoaderSuppressedSubject.subscribe(suppressed => {
      this.globalLoaderSuppressed.set(suppressed);
    });
    this.subscriptions.push(suppressSub);
  }

  /**
   * Method: Initializes the Flowable frontend configuration for the window.
   */
  private async initWindowFlowableFrontendConfiguration(): Promise<void> {
    await this.flowableVariablesService.initWindowFlowableFrontendConfiguration();
  }

  /**
   * Method: Initializes the routing subscription.
   */
  private initRoutingSubscription(): void {
    const routerSub = this.router.events.subscribe(async event => {
      await this.routerNavigationService.initRouterNavigation(event);
    });
    this.subscriptions.push(routerSub);

    const previousRouteSub = this.router.events.subscribe(event => {
      this.previousRouteService.init(event);
    });
    this.subscriptions.push(previousRouteSub);
  }

  /**
   * Method: Init process for auth user in application
   */
  private async initAuthProcess(): Promise<void> {
    if (this.isOidcCallbackPage()) {
      this.loading.set(false);
    } else {
      const user = await this.getUser();
      if (user) {
        await this.initWindowFlowableFrontendConfiguration();
      }
    }
  }

  /**
   * Method: Init process for auth iframe
   */
  private async initIframeAuthProcess(): Promise<void> {
    this.translate.use(this.defaultOpenLinkLang);
    this.iframeAuthService.setIframeLang(this.defaultOpenLinkLang);
    await this.iframeAuthService.iframeAuth(this.iframeService.getIframeHash());
  }

  /**
   * Method: Init process for auth shared link
   */
  private async initSharedLinkAuthProcess(): Promise<void> {
    this.translate.use(this.defaultOpenLinkLang);
    await this.sharedLinkService.sharedLinkAuth();
  }

  /**
   * Method: Get user data from oidc client library
   */
  private async getUser(): Promise<any> {
    return await this.oidcAuthService
      .getUser()
      .then(async currentUser => {
        if (!currentUser) {
          await this.oidcAuthService.login();
          return null;
        }
        await this.setTokenData(currentUser);
        this.translate.initLang();
        this.loading.set(false);
        return currentUser;
      })
      .catch(async () => {
        await this.oidcAuthService.login();
        return null;
      });
  }

  /**
   * Method: Update user token from oidc client library
   */
  private async renewToken(): Promise<void> {
    if (!this.jwtService.hasAccessToken()) {
      await this.oidcAuthService
        .renewToken()
        .then(async user => {
          await this.setTokenData(user);
          this.translate.initLang();
        })
        .catch(async () => {
          await this.oidcAuthService.login();
        });
    }
  }

  /**
   * Method: Sets the default window flowable endpoint.
   */
  private setDefaultWindowFlowableEndpoint(): void {
    this.flowableVariablesService.setDefaultWindowFlowableEndpoint();
  }

  /**
   * Method: Set token data after oidc auth
   * @param user - User data from the oidc client library
   */
  private async setTokenData(user: any): Promise<void> {
    this.jwtService.setToken(user);
    await this.oidcAuthService.checkAuthUser(user);
  }

  /**
   * Method: Check token for user
   */
  public isLogin(): boolean {
    return !!this.jwtService.hasAccessToken() || !!this.iframeAuthService.hasIframeToken() || this.isErrorPage();
  }

  /**
   * Method: Check access for header menu
   */
  public isHeader(): boolean {
    return this.headerService.hideHeader;
  }

  /**
   * Method: Check access for footer menu
   */
  public isHiddenFooter(): boolean {
    return this.footerService.isHiddenFooter;
  }

  /**
   * Method: Check error page
   */
  private isErrorPage(): boolean {
    return UrlFunctions.isPageByRouteUrl(Links.ERROR_LINK_URL);
  }

  /**
   * Method: Check iframe page
   */
  private isIframePage(): boolean {
    return UrlFunctions.isPageByRouteUrl(Links.IFRAME_LINK_URL);
  }

  /**
   * Method: Check open-component page
   */
  private isOpenComponentsPage(): boolean {
    return UrlFunctions.isPageByRouteUrl(Links.OPEN_COMPONENTS_LINK_URL);
  }

  /**
   * Method: Checks if the current page is a public entry point.
   */
  private isPublicEntryPointPage(): boolean {
    return this.publicEntrypointService.isPublicEntryPointPage();
  }

  /**
   * Method: Check oidc callback page
   */
  private isOidcCallbackPage(): boolean {
    return UrlFunctions.isPageByRouteUrl(Links.LINK_OIDC_CALLBACK);
  }

  /**
   * Method: Get browser name and version
   */
  private getBrowser(): string {
    return this.browserService.getBrowserVersion();
  }

  /**
   * Method: Detect loading app data
   */
  public isLoading(): boolean {
    return !this.isLogin() && this.loading();
  }

  /**
   * Method: Detect whether header and footer may render, which is only once the token exists
   */
  public isShellReady(): boolean {
    return !this.isLoading() && (this.isLogin() || !this.isOidcCallbackPage());
  }

  /**
   * Checks if the loader should be shown based on isLoading status and loadingRouter flag.
   * @returns {boolean} Whether the loader should be shown or not.
   */
  public showLoader(): boolean {
    if (this.globalLoaderSuppressed()) {
      return false;
    }
    return this.isLoading() || this.loadingRouter() || this.globalLoading();
  }

}
